/**
 * Task Card Component
 * 
 * Individual task card for the Kanban board
 */

import { UnifiedTask } from '@/types/tasks';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Calendar, 
  User, 
  ExternalLink, 
  CheckCircle2,
  AlertCircle,
  Clock,
  PoundSterling,
} from 'lucide-react';
import { format, isToday, isPast, isFuture } from 'date-fns';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

interface TaskCardProps {
  task: UnifiedTask;
  onStatusChange?: (taskId: string, newStatus: 'in_progress' | 'blocked' | 'completed') => void;
  onViewDetails?: (task: UnifiedTask) => void;
  onDragStart?: (task: UnifiedTask) => void;
  onDragEnd?: () => void;
}

const priorityColors = {
  Critical: 'bg-red-500 text-white hover:bg-red-600',
  High: 'bg-orange-500 text-white hover:bg-orange-600',
  Medium: 'bg-blue-500 text-white hover:bg-blue-600',
  Low: 'bg-gray-500 text-white hover:bg-gray-600',
};

const typeLabels = {
  collection_case: 'Collection',
  promise: 'Promise',
  dispute: 'Dispute',
  transaction: 'Receipt',
  customer_action: 'Action',
};

const typeColors = {
  collection_case: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  promise: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  dispute: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  transaction: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  customer_action: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
};

export function TaskCard({ task, onStatusChange, onViewDetails, onDragStart, onDragEnd }: TaskCardProps) {
  const navigate = useNavigate();
  
  const isOverdue = isPast(task.dueDate) && !isToday(task.dueDate) && task.status !== 'completed';
  const isDueToday = isToday(task.dueDate);
  const isUpcoming = isFuture(task.dueDate);

  const handleNavigate = () => {
    navigate(task.sourceUrl);
  };

  const handleComplete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onStatusChange?.(task.id, 'completed');
  };

  const handleInProgress = (e: React.MouseEvent) => {
    e.stopPropagation();
    onStatusChange?.(task.id, 'in_progress');
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move';
    onDragStart?.(task);
  };

  const handleDragEnd = () => {
    onDragEnd?.();
  };

  return (
    <Card 
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      className={cn(
        "group cursor-pointer transition-all hover:shadow-md",
        isOverdue && "border-l-4 border-l-red-500",
        isDueToday && "border-l-4 border-l-orange-500"
      )}
      onClick={() => onViewDetails?.(task)}
    >
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-sm truncate">{task.title}</h4>
            <p className="text-xs text-muted-foreground truncate mt-1">
              {task.description}
            </p>
          </div>
          <Badge 
            variant="secondary" 
            className={cn("shrink-0 text-xs", priorityColors[task.priority])}
          >
            {task.priority}
          </Badge>
        </div>

        {/* Customer & Amount */}
        <div className="flex items-center justify-between text-xs">
          <span className="font-medium truncate flex-1">{task.customerName}</span>
          {task.amount && (
            <span className="flex items-center gap-1 text-muted-foreground shrink-0 ml-2">
              <PoundSterling className="h-3 w-3" />
              {task.amount.toLocaleString()}
            </span>
          )}
        </div>

        {/* Type & Tags */}
        <div className="flex flex-wrap gap-1">
          <Badge variant="outline" className={cn("text-xs", typeColors[task.type])}>
            {typeLabels[task.type]}
          </Badge>
          {task.tags.slice(0, 2).map((tag, idx) => (
            <Badge key={idx} variant="outline" className="text-xs">
              {tag}
            </Badge>
          ))}
        </div>

        {/* Due Date */}
        <div className="flex items-center gap-2 text-xs">
          <div className={cn(
            "flex items-center gap-1",
            isOverdue && "text-red-600 dark:text-red-400 font-semibold",
            isDueToday && "text-orange-600 dark:text-orange-400 font-semibold",
            isUpcoming && "text-muted-foreground"
          )}>
            {isOverdue && <AlertCircle className="h-3 w-3" />}
            {isDueToday && <Clock className="h-3 w-3" />}
            {isUpcoming && <Calendar className="h-3 w-3" />}
            <span>
              {isOverdue && 'Overdue: '}
              {isDueToday && 'Due Today'}
              {isUpcoming && format(task.dueDate, 'MMM d')}
              {!isDueToday && !isUpcoming && format(task.dueDate, 'MMM d')}
            </span>
          </div>
          <div className="flex items-center gap-1 text-muted-foreground ml-auto">
            <User className="h-3 w-3" />
            <span className="truncate max-w-[80px]">{task.assignedTo}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t opacity-0 group-hover:opacity-100 transition-opacity">
          {task.status === 'todo' && (
            <Button
              size="sm"
              variant="outline"
              className="flex-1 h-7 text-xs"
              onClick={handleInProgress}
            >
              Start
            </Button>
          )}
          {task.status !== 'completed' && (
            <Button
              size="sm"
              variant="outline"
              className="flex-1 h-7 text-xs"
              onClick={handleComplete}
            >
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Complete
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            onClick={(e) => {
              e.stopPropagation();
              handleNavigate();
            }}
          >
            <ExternalLink className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
